import { Check, Copy, Loader2, Share2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { generateMessagePdf } from '@/lib/generatePdf';

const mode = import.meta.env.MODE;
const baseurl =
  mode === 'production'
    ? 'https://askawatch.cbre.com'
    : mode === 'staging'
    ? 'https://askawatch-test.cbre.com'
    : 'http://localhost:8000';

interface Props {
  messageId: string;
}

const ShareButton = ({ messageId }: Props) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleOpen = async (isOpen: boolean) => {
    setOpen(isOpen);
    // Generate & upload PDF only on first open
    if (isOpen && !shareUrl) {
      setLoading(true);
      try {
        const doc = await generateMessagePdf(messageId);
        const blob = doc.output('blob');
        const formData = new FormData();
        formData.append('file', blob, `message-${messageId}.pdf`);

        const res = await fetch(`${baseurl}/api/helper_router/share-pdf`, {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) throw new Error(`Upload failed: ${res.statusText}`);
        const { url } = await res.json();
        setShareUrl(url);
      } catch (err) {
        toast.error('Failed to generate share link: ' + String(err));
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                disabled={loading}
                className="text-muted-foreground"
              >
                {loading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Share2 className="h-4 w-4" />}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{loading ? 'Generating link…' : 'Share'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-80 p-3" align="start">
        <p className="text-xs font-medium text-foreground mb-2">Shareable PDF link</p>
        {loading ? (
          <div className="flex items-center gap-2 py-1">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">Generating link, please wait…</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={shareUrl ?? ''}
                className="text-xs h-8 flex-1 bg-muted cursor-default truncate"
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 shrink-0"
                onClick={handleCopy}
                disabled={!shareUrl}
              >
                {copied
                  ? <Check className="h-3.5 w-3.5 text-green-500" />
                  : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Link expires in 72 hours.</p>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export { ShareButton };
